// The Effect edge of the Operações screens (the DLQ inbox and the search by id): the one module
// that runs their calls. Each run hands the screen a Promise of the call's Result, so a refusal is data the screen renders (and React Query
// never retries a 403 as if it were a transient throw); a defect rejects the Promise.
import { queryOptions } from '@tanstack/react-query';
import { Effect, ManagedRuntime, Match, Option, Schema } from 'effect';
import type { Result } from 'effect';
import { FetchHttpClient } from 'effect/http';
import { dual } from 'effect/Function';
import { DlqDiscardRequest, DlqReplayRequest } from '@finnest/power-admin-contracts';
import type {
  DlqCursorToken,
  DlqInboxItem,
  DlqInboxPage,
  DlqReplayResults,
  OperationsSearchResult,
} from '@finnest/power-admin-contracts';
import { resolvePowerAdminApiBaseUrl } from '../../runtime/power-admin-api.ts';
import { discardDlq, listDlq, replayDlq } from './dlq.ts';
import type { OperationsOperation, OperationsRequestFailed } from './http.ts';
import { searchById } from './search.ts';

const operationsRuntime = ManagedRuntime.make(FetchHttpClient.layer);

/** What an Operações call answered: its value or why it failed. */
export type OperationsOutcome<A> = Result.Result<A, OperationsRequestFailed>;

// The fetch is read per run, as the browser (or a test) has it now; the API base is resolved per
// run too.
const runCall = <A>(
  operation: OperationsOperation<A>,
  signal?: AbortSignal,
): Promise<OperationsOutcome<A>> =>
  operationsRuntime.runPromise(
    Effect.suspend(() => operation(resolvePowerAdminApiBaseUrl())).pipe(
      Effect.result,
      Effect.provideService(FetchHttpClient.Fetch, globalThis.fetch),
    ),
    { signal },
  );

/** Where a page starts: the newest failures, or the cursor a previous page answered. */
export type PageStart =
  | { readonly _tag: 'Newest' }
  | { readonly _tag: 'After'; readonly cursor: DlqCursorToken };

export const NEWEST = { _tag: 'Newest' } as const satisfies PageStart;

const cursorOf = (start: PageStart): Option.Option<DlqCursorToken> =>
  Match.valueTags(start, {
    Newest: () => Option.none(),
    After: ({ cursor }) => Option.some(cursor),
  });

/** Every Operações read: the DLQ pages and the searches, to read them again after a replay. */
export const operationsQueryKey = ['power-admin', 'operations'] as const;

const KEY = [...operationsQueryKey, 'dlq'] as const;

/** Every DLQ page, to read them again after a replay or a discard. */
export const dlqQueryKey = KEY;

type DlqPageKey = readonly [...typeof KEY, 'page', PageStart];

export const dlqPageQueryOptions = (
  start: PageStart,
): ReturnType<
  typeof queryOptions<
    OperationsOutcome<DlqInboxPage>,
    Error,
    OperationsOutcome<DlqInboxPage>,
    DlqPageKey
  >
> =>
  queryOptions({
    queryKey: [...KEY, 'page', start] as const,
    queryFn: ({ signal }) => runCall(listDlq(cursorOf(start)), signal),
    retry: false,
  });

type SearchKey = readonly [...typeof operationsQueryKey, 'search', string];

/** What Power keeps under `id`, as the operator typed it. */
export const searchQueryOptions = (
  id: string,
): ReturnType<
  typeof queryOptions<
    OperationsOutcome<OperationsSearchResult>,
    Error,
    OperationsOutcome<OperationsSearchResult>,
    SearchKey
  >
> =>
  queryOptions({
    queryKey: [...operationsQueryKey, 'search', id] as const,
    queryFn: ({ signal }) => runCall(searchById(id), signal),
    retry: false,
  });

// A write is not cut off by the screen going away: its own deadline (in the client) ends it as an
// outcome the screen reports as uncertain. No signal is passed, so nothing aborts it.

/** Replays `request`'s events. */
export const replayOutcome = (
  request: DlqReplayRequest,
): Promise<OperationsOutcome<DlqReplayResults>> => runCall(replayDlq(request));

/** Discards `request`'s event for good. */
export const discardOutcome = (request: DlqDiscardRequest): Promise<OperationsOutcome<void>> =>
  runCall(discardDlq(request));

const CLOSED = { onExcessProperty: 'error' } as const;

/**
 * The replay body for `items`, acknowledging events older than 24 hours or not: refused when
 * there is none or more than the batch bound.
 */
export const replayRequestOf: {
  (
    acknowledgeStale: boolean,
  ): (items: readonly DlqInboxItem[]) => Result.Result<DlqReplayRequest, Schema.SchemaError>;
  (
    items: readonly DlqInboxItem[],
    acknowledgeStale: boolean,
  ): Result.Result<DlqReplayRequest, Schema.SchemaError>;
} = dual(
  2,
  (
    items: readonly DlqInboxItem[],
    acknowledgeStale: boolean,
  ): Result.Result<DlqReplayRequest, Schema.SchemaError> =>
    Schema.decodeUnknownResult(DlqReplayRequest)(
      {
        items: items.map(({ tenantId, eventId, dlqId }) => ({ tenantId, eventId, dlqId })),
        acknowledgeStale,
      },
      CLOSED,
    ),
);

/**
 * The discard body for `item` with the operator's reason, trimmed: refused while the reason is
 * outside the contract's 10 to 500 characters.
 */
export const discardRequestOf: {
  (reason: string): (item: DlqInboxItem) => Result.Result<DlqDiscardRequest, Schema.SchemaError>;
  (item: DlqInboxItem, reason: string): Result.Result<DlqDiscardRequest, Schema.SchemaError>;
} = dual(
  2,
  (item: DlqInboxItem, reason: string): Result.Result<DlqDiscardRequest, Schema.SchemaError> =>
    Schema.decodeResult(DlqDiscardRequest)(
      { tenantId: item.tenantId, eventId: item.eventId, dlqId: item.dlqId, reason: reason.trim() },
      CLOSED,
    ),
);
